import React, { useMemo, useRef } from 'react'
import classNames from 'classnames'
import addDays from 'date-fns/addDays'
import { LocaleUtils } from 'react-day-picker'

import { firstWODDate } from 'ui-component-library/utils/helpers'

import ArrowDown from 'ui-component-library/icons/arrow-down.svg'
import ArrowLeft from 'ui-component-library/icons/arrow-left.svg'
import ArrowRight from 'ui-component-library/icons/arrow-right.svg'

import style from './style.scss'

export type YearMonthProps = {
  monthText: string
  showNextMonthLabelText: string
  showPreviousMonthLabelText: string
  yearText: string
}

type Props = YearMonthProps & {
  className?: string
  date: Date
  nextMonth: Date
  onChange(date: Date): void
  previousMonth: Date
  todayDate: Date
  yearMonthFormId?: string
}

YearMonthForm.defaultProps = {
  className: '',
  yearMonthFormId: undefined,
}

function YearMonthForm(props: Props) {
  const {
    className,
    date,
    onChange,
    monthText,
    nextMonth,
    previousMonth,
    todayDate,
    showNextMonthLabelText,
    showPreviousMonthLabelText,
    yearMonthFormId,
    yearText,
  } = props
  const months = LocaleUtils.getMonths()
  const monthRef = useRef<HTMLSelectElement | null>(null)
  const yearRef = useRef<HTMLSelectElement | null>(null)

  const generateYears = () => {
    const years = []
    for (
      let i = firstWODDate.getFullYear();
      i <= addDays(todayDate, 1).getFullYear();
      i += 1
    ) {
      years.push(i)
    }
    return years
  }

  const years = useMemo(generateYears, [])

  const handleChange = () => {
    if (monthRef.current && yearRef.current) {
      onChange(
        new Date(Number(yearRef.current.value), Number(monthRef.current.value)),
      )
    }
  }

  return (
    <div className={classNames(style.wrapper, className)} id={yearMonthFormId}>
      <span className={style.dropdown}>
        <label className={style.hidden} htmlFor='date-picker-month-select'>
          {monthText}
        </label>
        <select
          ref={monthRef}
          className={style.select}
          id='date-picker-month-select'
          name='month'
          value={date.getMonth()}
          onChange={handleChange}
        >
          {months.map((month, i) => (
            <option key={month} value={i}>
              {month}
            </option>
          ))}
        </select>
        <ArrowDown className={style.icon} />
      </span>
      <span className={style.dropdown}>
        <label className={style.hidden} htmlFor='date-picker-year-select'>
          {yearText}
        </label>
        <select // $FlowFixMe
          ref={yearRef}
          className={style.select}
          id='date-picker-year-select'
          name='year'
          value={date.getFullYear()}
          onChange={handleChange}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        <ArrowDown className={style.icon} />
      </span>
      <span className={style.arrowNextPrevContainer}>
        <button
          aria-label={showPreviousMonthLabelText}
          className={style.button}
          type='button'
          onClick={() => onChange(previousMonth)}
        >
          <ArrowLeft className={style.horizontalArrows} />
        </button>
        <button
          aria-label={showNextMonthLabelText}
          className={style.button}
          type='button'
          onClick={() => onChange(nextMonth)}
        >
          <ArrowRight className={style.horizontalArrows} />
        </button>
      </span>
    </div>
  )
}

export default React.memo<Props>(YearMonthForm)
